import { useCallback, useState } from 'react';

import { downloadFile } from '../shared/download';
import { logger } from '../shared/utils/logger';

/**
 * Shared download-with-feedback wrapper over `downloadFile`. Tracks
 * which URL is being resolved/fetched so every download surface (FileRow,
 * invoice buttons, certificate tables) can show the same pending state
 * instead of leaving the tap feeling dead while the signed URL resolves.
 */
export const useSignedUrlDownload = () => {
	const [downloadingUrl, setDownloadingUrl] = useState<string | null>(null);

	/**
	 * `resolveFresh` (opcional) pide al backend un enlace firmado AL MOMENTO del
	 * clic, para archivos cuyo enlace guardado puede estar vencido o cerrado
	 * (llaves privadas, t-248). Si falla, se usa `rawUrl` como antes.
	 */
	const download = useCallback(
		async (rawUrl?: string | null, resolveFresh?: () => Promise<string>) => {
			if (!rawUrl || downloadingUrl) return;
			setDownloadingUrl(rawUrl);
			try {
				let target = rawUrl;
				if (resolveFresh) {
					try {
						target = (await resolveFresh()) || rawUrl;
					} catch (error) {
						logger.warn('credential_file_fresh_url', error);
					}
				}
				await downloadFile({ url: target });
			} finally {
				setDownloadingUrl(null);
			}
		},
		[downloadingUrl],
	);

	const isDownloading = useCallback(
		(rawUrl?: string | null) => !!rawUrl && downloadingUrl === rawUrl,
		[downloadingUrl],
	);

	return { download, isDownloading, isAnyDownloading: !!downloadingUrl };
};
